import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Chip,
  CircularProgress,
  useTheme,
} from '@mui/material';
import { KeyboardArrowDown as KeyboardArrowDownIcon } from '@mui/icons-material';

const CustomAutocomplete = ({
  label,
  placeholder,
  options = [],
  loading = false,
  onChange,
  getOptionLabel,
  multiple = true,
  helpText,
  selectedChips = [],
  onChipDelete,
  chipColor = 'primary',
  getChipColor, // Function to dynamically determine chip color
  zIndex = 1,
  openDirection = 'up', // 'up' or 'down'
}) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isHovered, setIsHovered] = useState(false);
  
  // Helper function to get background color based on theme
  const getBackgroundColor = (hover = false) => {
    if (theme.palette.mode === 'dark') {
      return hover ? 'rgba(255, 255, 255, 0.05)' : 'transparent';
    } else {
      return hover ? 'rgba(0, 0, 0, 0.02)' : 'transparent';
    }
  };

  // Закрытие dropdown при клике вне
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (open) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [open]);

  // Фильтрация опций по поисковому запросу
  const filteredOptions = options.filter(option => {
    const label = getOptionLabel(option);
    return label.toLowerCase().includes(searchQuery.toLowerCase());
  });

  // Проверка, выбрана ли опция
  const isSelected = (option) => {
    return selectedChips.some(
      item => (item.id || item._id) === (option.id || option._id)
    );
  };

  // Обработка клика на опцию
  const handleOptionClick = (option) => {
    const isAlreadySelected = isSelected(option);
    
    if (isAlreadySelected) {
      // Удаляем опцию
      onChange(
        selectedChips.filter(
          item => (item.id || item._id) !== (option.id || option._id)
        )
      );
    } else {
      // Добавляем опцию
      onChange([...selectedChips, option]);
    }
  };

  return (
    <Box sx={{ position: 'relative', zIndex }}>
      {label && (
        <Typography 
          variant="body2" 
          fontWeight={500} 
          mb={1}
          sx={{ color: 'text.label' }}
        >
          {label}
        </Typography>
      )}

      {/* Выбранные чипы */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
        {selectedChips.map((item, index) => {
          const label = getOptionLabel(item);
          const chipColorValue = getChipColor ? getChipColor(item, index) : chipColor;

          // Custom styles based on chip type
          const getChipStyles = () => {
            if (chipColorValue === 'manual') {
              // Manual professions - use secondary color (orange/amber)
              return {
                bgcolor: theme.palette.secondary.main + '1A', // 10% opacity
                color: theme.palette.secondary.main,
                border: '1px solid',
                borderColor: theme.palette.secondary.main,
                '&:hover': {
                  bgcolor: theme.palette.secondary.main + '3D', // 24% opacity
                },
                '& .MuiChip-deleteIcon': {
                  color: theme.palette.secondary.main,
                  '&:hover': {
                    color: theme.palette.secondary.main,
                    opacity: 0.8
                  }
                }
              };
            } else if (chipColorValue === 'auto') {
              // Auto professions - use primary color (blue)
              return {
                bgcolor: theme.palette.primary.main + '1A', // 10% opacity
                color: theme.palette.primary.main,
                border: '1px solid',
                borderColor: theme.palette.primary.main,
                '&:hover': {
                  bgcolor: theme.palette.primary.main + '3D', // 24% opacity
                },
                '& .MuiChip-deleteIcon': {
                  color: theme.palette.primary.main,
                  '&:hover': {
                    color: theme.palette.primary.main,
                    opacity: 0.8
                  }
                }
              };
            }
            return {};
          };

          return (
            <Chip
              key={item._id || item.id || index}
              label={label}
              onDelete={onChipDelete ? () => onChipDelete(index) : undefined}
              color={chipColorValue === 'manual' || chipColorValue === 'auto' ? undefined : chipColorValue}
              sx={{
                m: 0,
                borderRadius: '6px',
                height: '28px',
                '& .MuiChip-label': {
                  px: 1.5,
                  py: 0.5,
                  fontSize: '0.8125rem'
                },
                ...getChipStyles()
              }}
            />
          );
        })}
      </Box>

      {/* Выпадающий список */}
      <Box
        onClick={(e) => {
          e.stopPropagation();
          setOpen(!open);
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        sx={{
          p: '0 16px',
          border: '1px solid',
          borderColor: open ? 'primary.main' : 'divider',
          borderRadius: 3,
          bgcolor: getBackgroundColor(isHovered),
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          height: '48px',
          transition: 'all 0.2s',
          '&:hover': {
            borderColor: 'primary.main',
            bgcolor: getBackgroundColor(true),
          }
        }}
      >
        <Typography 
          variant="body1" 
          sx={{ 
            fontSize: '0.875rem',
            color: 'text.primary'
          }}
        >
          {placeholder}
        </Typography>
        <KeyboardArrowDownIcon sx={{ 
          fontSize: 20, 
          color: 'text.secondary',
          transition: 'transform 0.2s',
          transform: open ? 'rotate(180deg)' : 'rotate(0deg)'
        }} />
      </Box>

      {/* Dropdown меню */}
      {open && (
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            position: 'absolute',
            ...(openDirection === 'down'
              ? { top: '100%', mt: 1 }
              : { bottom: '100%', mb: 1 }
            ),
            left: 0,
            right: 0,
            bgcolor: 'background.paper',
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: 3,
            zIndex: 3000,
            maxHeight: 300,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {/* Поле поиска */}
          <Box sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
            <TextField
              fullWidth
              size="small"
              placeholder={placeholder}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2
                }
              }}
            />
          </Box>

          {/* Список опций */}
          <Box sx={{ overflowY: 'auto', p: 1 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              filteredOptions.map((option) => {
                const selected = isSelected(option);
                const label = getOptionLabel(option);

                return (
                  <Box
                    key={option._id || option.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOptionClick(option);
                    }}
                    sx={{
                      p: 1,
                      cursor: 'pointer',
                      borderRadius: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      '&:hover': {
                        bgcolor: 'action.hover'
                      }
                    }}
                  >
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        border: '2px solid',
                        borderColor: selected ? 'primary.main' : 'grey.400',
                        borderRadius: 0.5,
                        mr: 1.5,
                        bgcolor: selected ? 'primary.main' : 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}
                    >
                      {selected && (
                        <Typography sx={{ color: 'white', fontSize: 12 }}>✓</Typography>
                      )}
                    </Box>
                    <Typography variant="body2">
                      {label}
                    </Typography>
                  </Box>
                );
              })
            )}
          </Box>
        </Box>
      )}

      {/* Подсказка */}
      {helpText && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {helpText}
        </Typography>
      )}
    </Box>
  );
};

export default CustomAutocomplete;

